import { useEffect, useRef, useState } from "react";
import GoBack from "../components/GoBack";
import { itemDisplayName } from "../utils/storeItem";
import useStore from "../hooks/useStore";
import { toast } from "react-toastify";
import useFlow from "../hooks/useFlow";

const SEARCH_DEBOUNCE_MS = 350;
const MIN_SEARCH_LENGTH = 2;

export default function Store() {
  const {
    setShowPopularItemModal,
    setSelectedPopularItem,
    setShowGiftCardModal,
    setSelectedGiftCard,
    searchItemById,
  } = useStore();

  const productCodeRef = useRef(null);

  useEffect(() => {
    productCodeRef.current.focus();
  }, []);

  const { locationPreferences } = useFlow();

  const [productTag, setProductTag] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  // Guards against a slow early request landing after a faster later one.
  const searchSeq = useRef(0);

  const itemClickHandler = (item) => {
    if (item.type === "giftCard") {
      setSelectedGiftCard(item);
      setShowGiftCardModal(true);
    } else {
      setSelectedPopularItem(item);
      setShowPopularItemModal(true);
    }
  };

  const clearSearch = () => {
    searchSeq.current++;
    setProductTag("");
    setResults([]);
    setSearched(false);
    setLoading(false);
    productCodeRef.current?.focus();
  };

  const runSearch = async (term) => {
    const seq = ++searchSeq.current;
    setLoading(true);

    try {
      const resp = await searchItemById(term);
      if (seq !== searchSeq.current) return;
      setResults(resp.data || []);
      setSearched(true);
    } catch (e) {
      if (seq !== searchSeq.current) return;
      toast.error(`Something went wrong! ${e?.message}`);
      setResults([]);
      setSearched(true);
    } finally {
      if (seq === searchSeq.current) setLoading(false);
    }
  };

  // Instant search: debounce keystrokes, then show the matches as cards.
  useEffect(() => {
    const term = productTag.trim();

    if (term.length < MIN_SEARCH_LENGTH) {
      searchSeq.current++;
      setResults([]);
      setSearched(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timer = setTimeout(() => runSearch(term), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [productTag]); // eslint-disable-line react-hooks/exhaustive-deps

  // A live search takes over the page below the fold — the browse sections
  // would otherwise compete with the results for the same space.
  const searchActive = productTag.trim().length >= MIN_SEARCH_LENGTH;

  const topItems = locationPreferences?.store?.topItems?.filter(Boolean) || [];
  const bottomItems =
    locationPreferences?.store?.bottomItems?.filter(Boolean) || [];

  return (
    <div className="container d-block storeContainer">
      <GoBack />

      {/* Sits low on the page via a viewport-height offset rather than by
          centering, so the field lands in the same place every time no matter
          what renders underneath it. */}
      <div
        id="store-search"
        className="d-flex flex-column w-100 align-items-center"
        style={{ paddingTop: "20vh" }}
      >
        <span className="text-center" data-back-screen-id="0" data-cancel="0">
          <h2 className="fw-light m-0">
            <b>Search by name</b>
            <br />
            or product code
          </h2>
        </span>

        <div className="position-relative py-5" style={{ width: 350 }}>
          <input
            type="text"
            className="form-control form-control-lg"
            style={{ paddingRight: 48 }}
            data-testid="product-search-input"
            placeholder="Water or 321-12"
            value={productTag}
            onChange={(e) => setProductTag(e.target.value)}
            autoComplete="off"
            ref={productCodeRef}
          />

          {/* Clear sits inside the field, and only once there's something to
              clear — the field's right padding reserves its space. */}
          {productTag.length > 0 && (
            <button
              type="button"
              className="btn btn-link text-secondary text-decoration-none p-0 position-absolute top-50 end-0 translate-middle-y me-2 d-inline-flex align-items-center justify-content-center"
              style={{ width: 44, height: 44, fontSize: 22 }}
              data-testid="product-search-clear-btn"
              aria-label="Clear search"
              onClick={clearSearch}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>

        {loading && results.length === 0 && (
          <p className="text-muted text-center m-0 pt-4">Searching...</p>
        )}

        {!loading && searched && results.length === 0 && (
          <p
            className="text-muted text-center m-0 pt-4"
            data-testid="product-search-empty"
          >
            No products match &quot;{productTag.trim()}&quot;
          </p>
        )}
      </div>

      <div className="py-5" id="store-results">
        {searchActive ? (
          <div className="row justify-content-center g-4">
            {results.map((product) => (
              <div
                className="col-12 col-md-6 col-xl-4"
                key={`${product.id}-${product.color || ""}-${
                  product.size || ""
                }`}
              >
                <div
                  className="h-100 p-4 bg-white shadow-sm rounded-4 border d-flex flex-column"
                  role="button"
                  data-testid="product-result-card"
                  onClick={() => itemClickHandler(product)}
                >
                  <h4 className="fw-bold m-0">{product.name}</h4>
                  <p className="m-0 pt-2 fs-4">
                    <small className="fw-light">$</small>
                    {product.price}
                  </p>
                  <p className="m-0 pt-2">
                    <small>#{product.id}</small>
                    {product.color && (
                      <span className="badge rounded-pill bg-secondary text-white ms-2">
                        {product.color}
                      </span>
                    )}
                    {product.size && (
                      <span className="badge rounded-pill bg-secondary text-white ms-2">
                        {product.size}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="row justify-content-center">
            <div className="col-12">
              <div className="row justify-content-center g-4">
                {topItems.map((item, index) => (
                  <div className="col-12 col-md-6 col-lg-3" key={index}>
                    <div
                      className="get-package store-card h-100 p-5 text-center border rounded-4 bg-white shadow-sm"
                      role="button"
                      data-testid="store-item-card"
                      onClick={() => itemClickHandler(item)}
                    >
                      {/* Two lines' worth of height whether the name wraps or
                          not, so every card's price sits on the same line. */}
                      <h4
                        className="lh-sm m-0"
                        style={{ minHeight: "2.5em" }}
                      >
                        {itemDisplayName(item)}
                      </h4>
                      {item.sub && (
                        <p className="fw-light opacity-75 m-0 pt-2">
                          {item.sub}
                        </p>
                      )}

                      <div className="pt-2">
                        {item.oldPrice > item.price && (
                          <span className="fs-5 fw-light opacity-50 text-decoration-line-through me-2">
                            ${item.oldPrice}
                          </span>
                        )}
                        <span className="fs-3 fw-normal lh-1">
                          <small className="fs-6 fw-light opacity-50 align-top">
                            $
                          </small>
                          {item.price}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                {bottomItems.length > 0 && (
                  <h5 className="text-center fw-light pt-5 pb-4">
                    {locationPreferences?.store?.bottomLabel}
                  </h5>
                )}
                <div
                  id="gift-cards"
                  className="row align-items-center justify-content-center g-4 mb-5"
                >
                  {bottomItems.map((item, index) => (
                    <div className="col-12 col-md-2" key={index}>
                      <div
                        className="get-gift-card ratio ratio-1x1 text-center border rounded-4 bg-white shadow-sm"
                        role="button"
                        data-testid="gift-card-item-card"
                        onClick={() => itemClickHandler(item)}
                      >
                        <span>
                          <h4 className="fw-light m-0">
                            {itemDisplayName(item)}
                          </h4>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
